"use client";

import React from "react";
import { ArrowRight, CircleAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
const wp_term_query_params = {
  taxonomy: "product_cat",
  per_page: 3,
  orderby: "count",
  order: "desc"
};
const wp_term_query = new WP_Term_Query(wp_term_query_params);
function FleetCategoriesConsumer() {
  const {
    terms,
    loading,
    error,
    refetch
  } = useEcommerceTerms();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[400px] w-full animate-pulse rounded-2xl bg-muted" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-muted/30 p-12 text-center">
        <CircleAlert className="mb-4 h-10 w-10 text-muted-foreground" />
        <p className="mb-4 text-lg font-medium text-foreground">
          Failed to load fleet categories.
        </p>
        <Button variant="outline" onClick={refetch}>
          Try Again
        </Button>
      </div>;
  }
  if (!terms || terms.length === 0) {
    return <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-muted/30 p-12 text-center">
        <p className="text-lg font-medium text-muted-foreground">
          No fleet categories available at the moment.
        </p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {terms.map((term, i) => <Link key={term.id} to={term.link || "#"} data-index={i} className="group relative block h-[400px] w-full overflow-hidden rounded-2xl bg-secondary">
          {}
          {term.image_url ? <img src={term.image_url} alt={term.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" loading="lazy" /> : <div className="absolute inset-0 bg-muted transition-transform duration-700 ease-out group-hover:scale-110" />}

          {}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 transition-opacity duration-500 group-hover:opacity-90" />

          {}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
            <span className="select-none whitespace-nowrap text-[6rem] font-black tracking-tighter text-white/10 transition-opacity duration-500 group-hover:opacity-20 md:text-[8rem]">
              {term.name.toUpperCase()}
            </span>
          </div>

          {}
          <div className="absolute inset-0 z-10 flex flex-col justify-end p-8">
            <h3 className="mb-2 text-3xl font-bold text-white">{term.name}</h3>
            {term.description && <p className="mb-6 line-clamp-2 text-sm text-white/70">
                {term.description}
              </p>}
            <div className="mt-auto flex items-center pt-4">
              <span className="inline-flex items-center text-sm font-semibold text-white/90 transition-colors group-hover:text-white">
                View All
                <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </div>
          </div>
        </Link>)}
    </div>;
}
export default function FleetCategories() {
  return <section id="fleet-categories" className="bg-background py-20 md:py-32">
      <div className="container mx-auto px-4">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold tracking-tight text-foreground md:text-5xl">
            Explore Our Fleet
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            Choose the perfect vehicle category for your gig work. From fuel-efficient compacts to spacious cargo vans, we have the right ride for your hustle.
          </p>
        </div>

        <EcommerceTermsProvider wp_term_query={wp_term_query}>
          <FleetCategoriesConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>;
}